import { Id } from '@/infra/id'
import { collection } from '@/infra/collection'
import { onRpcNotFoundReturnNull } from '@/utils/grpc-util'
import { type JobsApi } from '@/features/jobs/api/job-api'
import {
  CreateDependentProfileRequest,
  GetDependentProfileForJobRequest,
  GetPersonalProfileForJobRequest,
  GetSpousalProfileForJobRequest,
  SavePersonalProfileRequest,
  SaveSpousalProfileRequest,
  UpdateDependentProfileRequest,
} from '@taxfyle/api-internal/internal/individual_profile_pb'
import type { ContactInfo } from '@/features/jobs/info-gathering/ContactInfo'
import { mapContactInfoToProto } from '@/features/jobs/info-gathering/contact-info-mappers'
import type { PersonalInfo } from './PersonalInfo'
import {
  PersonalProfileVersion,
  PersonalProfileVersionId,
} from './PersonalProfileVersion'
import {
  SpousalProfileVersion,
  SpousalProfileVersionId,
} from './SpousalProfileVersion'
import {
  DependentProfileVersion,
  DependentProfileVersionId,
} from './DependentProfileVersion'
import {
  mapDependentProfileVersionProtoToProps,
  mapPersonalInfoToProto,
  mapPersonalProfileVersionProtoToProps,
  mapSpousalProfileVersionProtoToProps,
} from './individual-profile-mappers'

/**
 * Manages the personal, spousal and dependent profiles captured for jobs.
 */
export class IndividualProfileStore {
  readonly personalProfileVersions = collection({
    create: () => new PersonalProfileVersion(),
    getDataId: (d) => PersonalProfileVersionId.from(d.id, d.version),
    getModelId: (m) => PersonalProfileVersionId.from(m.id, m.version),
  })

  readonly spousalProfileVersions = collection({
    create: () => new SpousalProfileVersion(),
    getDataId: (d) => SpousalProfileVersionId.from(d.id, d.version),
    getModelId: (m) => SpousalProfileVersionId.from(m.id, m.version),
  })

  readonly dependentProfileVersions = collection({
    create: () => new DependentProfileVersion(),
    getDataId: (d) => DependentProfileVersionId.from(d.id, d.version),
    getModelId: (m) => DependentProfileVersionId.from(m.id, m.version),
  })

  constructor(private readonly _api: JobsApi) {}

  /**
   * Gets a personal profile version.
   */
  getPersonalProfile(id: Id, version: number) {
    return this.personalProfileVersions.get(
      PersonalProfileVersionId.from(id, version),
    )
  }

  /**
   * Gets a spousal profile version.
   */
  getSpousalProfile(id: Id, version: number) {
    return this.spousalProfileVersions.get(
      SpousalProfileVersionId.from(id, version),
    )
  }

  /**
   * Gets a dependent profile version.
   */
  getDependentProfile(id: Id, version: number) {
    return this.dependentProfileVersions.get(
      DependentProfileVersionId.from(id, version),
    )
  }

  /**
   * Fetches the personal profile provided to a job.
   */
  async fetchPersonalProfileVersionForJob(
    jobId: Id,
    signal?: AbortSignal,
  ): Promise<PersonalProfileVersion | null> {
    const request = new GetPersonalProfileForJobRequest().setJobId(
      Id.assert(jobId),
    )
    const response = await this._api.individualProfiles
      .getPersonalProfileForJob(request)
      .catch(onRpcNotFoundReturnNull)
    signal?.throwIfAborted()
    if (!response) {
      return null
    }

    return this.personalProfileVersions.set(
      mapPersonalProfileVersionProtoToProps(response),
    )
  }

  /**
   * Saves a new personal profile version. Callers link the version to the
   * job's supporting item with
   * `InfoGatheringStore.provideSupportingItemPersonalInfo`, which is what
   * makes it visible through `GetPersonalProfileForJob`.
   */
  async savePersonalProfileVersion(
    input: SavePersonalProfileOptions,
  ): Promise<PersonalProfileVersion> {
    const request = new SavePersonalProfileRequest()
      .setWorkspaceId(Id.assert(input.workspaceId))
      .setOwnerUserId(Id.assert(input.ownerUserId))
      .setOccupation(input.occupation)
      .setPersonalInfo(mapPersonalInfoToProto(input.personalInfo))
      .setContactInfo(mapContactInfoToProto(input.contactInfo))

    const response =
      await this._api.individualProfiles.savePersonalProfile(request)

    return this.personalProfileVersions.set({
      id: Id(response.getId()),
      version: response.getVersion(),
      workspaceId: input.workspaceId,
      ownerUserId: input.ownerUserId,
      occupation: input.occupation,
      personalInfo: input.personalInfo,
      contactInfo: input.contactInfo,
    })
  }

  /**
   * Saves a new spousal profile version.
   */
  async saveSpousalProfileVersion(
    input: SaveSpousalProfileOptions,
  ): Promise<SpousalProfileVersion> {
    const request = new SaveSpousalProfileRequest()
      .setWorkspaceId(Id.assert(input.workspaceId))
      .setOwnerUserId(Id.assert(input.ownerUserId))
      .setOccupation(input.occupation)
      .setPersonalInfo(mapPersonalInfoToProto(input.personalInfo))
      .setContactInfo(mapContactInfoToProto(input.contactInfo))

    const response =
      await this._api.individualProfiles.saveSpousalProfile(request)

    return this.spousalProfileVersions.set({
      id: Id(response.getId()),
      version: response.getVersion(),
      workspaceId: input.workspaceId,
      ownerUserId: input.ownerUserId,
      occupation: input.occupation,
      personalInfo: input.personalInfo,
      contactInfo: input.contactInfo,
    })
  }

  /**
   * Fetches the spousal profile provided to a job.
   */
  async fetchSpousalProfileVersionForJob(
    jobId: Id,
    signal?: AbortSignal,
  ): Promise<SpousalProfileVersion | null> {
    const request = new GetSpousalProfileForJobRequest().setJobId(
      Id.assert(jobId),
    )
    const response = await this._api.individualProfiles
      .getSpousalProfileForJob(request)
      .catch(onRpcNotFoundReturnNull)
    signal?.throwIfAborted()
    if (!response) {
      return null
    }

    return this.spousalProfileVersions.set(
      mapSpousalProfileVersionProtoToProps(response),
    )
  }

  /**
   * Fetches a dependent profile provided to a job.
   */
  async fetchDependentProfileVersionForJob(
    id: Id,
    jobId: Id,
    signal?: AbortSignal,
  ): Promise<DependentProfileVersion | null> {
    const request = new GetDependentProfileForJobRequest()
      .setId(Id.assert(id))
      .setJobId(Id.assert(jobId))
    const response = await this._api.individualProfiles
      .getDependentProfileForJob(request)
      .catch(onRpcNotFoundReturnNull)
    signal?.throwIfAborted()
    if (!response) {
      return null
    }

    return this.dependentProfileVersions.set(
      mapDependentProfileVersionProtoToProps(response),
    )
  }

  /**
   * Creates a dependent profile.
   */
  async createDependentProfile(
    input: CreateDependentProfileOptions,
  ): Promise<DependentProfileVersion> {
    const request = new CreateDependentProfileRequest()
      .setWorkspaceId(Id.assert(input.workspaceId))
      .setOwnerUserId(Id.assert(input.ownerUserId))
      .setRelationship(input.relationship)
      .setPersonalInfo(mapPersonalInfoToProto(input.personalInfo))

    const response =
      await this._api.individualProfiles.createDependentProfile(request)

    return this.dependentProfileVersions.set({
      id: Id(response.getId()),
      version: response.getVersion(),
      workspaceId: input.workspaceId,
      ownerUserId: input.ownerUserId,
      relationship: input.relationship,
      personalInfo: input.personalInfo,
    })
  }

  /**
   * Saves a new version of a dependent profile.
   */
  async updateDependentProfile(
    input: UpdateDependentProfileOptions,
  ): Promise<DependentProfileVersion> {
    const request = new UpdateDependentProfileRequest()
      .setId(Id.assert(input.id))
      .setRelationship(input.relationship)
      .setPersonalInfo(mapPersonalInfoToProto(input.personalInfo))

    const response =
      await this._api.individualProfiles.updateDependentProfile(request)

    return this.dependentProfileVersions.set({
      id: Id(response.getId()),
      version: response.getVersion(),
      workspaceId: input.workspaceId,
      ownerUserId: input.ownerUserId,
      relationship: input.relationship,
      personalInfo: input.personalInfo,
    })
  }
}

/**
 * Options for saving a personal profile version.
 */
export interface SavePersonalProfileOptions {
  workspaceId: Id
  ownerUserId: Id
  occupation: string
  personalInfo: PersonalInfo
  contactInfo: ContactInfo
}

/**
 * Options for saving a spousal profile version.
 */
export type SaveSpousalProfileOptions = SavePersonalProfileOptions

/**
 * Options for creating a dependent profile.
 */
export interface CreateDependentProfileOptions {
  workspaceId: Id
  ownerUserId: Id
  relationship: string
  personalInfo: PersonalInfo
}

/**
 * Options for saving a new version of a dependent profile.
 */
export interface UpdateDependentProfileOptions extends CreateDependentProfileOptions {
  id: Id
}
